import {
  Button,
  Flex,
  Heading,
  NumberInput,
  NumberInputField,
  Table,
  Tbody,
  Td,
  Text,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../api";
import useFetchPrices from "../../hooks/api/useFetchPrices";
import useShowToast from "../../hooks/useShowToast";

const SettingsPrices = (): ReactElement => {
  const { t } = useTranslation();
  const { prices, refetchPrices } = useFetchPrices();
  const { successToast, errorToast } = useShowToast();
  const [priceValues, setPriceValues] = useState<{ [key: number]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const initialValues: { [key: number]: string } = {};
    prices?.forEach(price => {
      initialValues[price.id] = price.value.toString();
    });
    setPriceValues(initialValues);
  }, [prices]);

  const handlePriceChange = (priceId: number, valueAsString: string) => {
    setPriceValues(prev => ({
      ...prev,
      [priceId]: valueAsString,
    }));
  };

  const handleSave = async () => {
    const payload = Object.entries(priceValues).map(([id, value]) => ({
      id: Number(id),
      value: parseFloat(value) || 0,
    }));

    setIsLoading(true);

    const result = await api.updatePrices({ prices: payload });

    if (!result || api.error) {
      setIsLoading(false);
      errorToast({
        title: t("settings.prices.error_title"),
        description: t("settings.prices.error_message"),
      });
      return;
    }

    await refetchPrices();
    setIsLoading(false);
    successToast({
      title: t("settings.prices.success_title"),
      description: t("settings.prices.success_message"),
    });
  };

  return (
    <>
      <Heading size="md" mb={3}>
        {t("settings.prices.title")}
      </Heading>

      <VStack spacing={4} align="stretch" maxWidth="600px">
        <Table size="sm" variant="simple">
          <Tbody>
            {prices?.map(price => (
              <Tr key={price.id}>
                <Td fontSize="sm" fontWeight="medium" py={1}>
                  {price.description}
                </Td>
                <Td width="120px" textAlign="right" py={1}>
                  <Flex alignItems="center" justifyContent="flex-end" gap={1}>
                    <NumberInput
                      size="sm"
                      width="80px"
                      defaultValue={0}
                      precision={2}
                      step={0.5}
                      value={priceValues[price.id]}
                      onChange={v => handlePriceChange(price.id, v)}
                    >
                      <NumberInputField />
                    </NumberInput>
                    <Text fontSize="sm">€/h</Text>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        <Button
          mt={2}
          size="md"
          colorScheme="blue"
          onClick={handleSave}
          alignSelf="flex-start"
          isLoading={isLoading}
        >
          {t("settings.general.save")}
        </Button>
      </VStack>
    </>
  );
};

export default SettingsPrices;
